import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { ESF_STATIONLIST, MainContentPaddingTypes, NewInput, NewModal, NewSelect, SystemControlsInner, ActiveStationsCards } from "../..";
import { dispatchErrorWithTimeout, useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { useSectionOptions } from "../../_Components";
import { MenuFloats, NewFieldStyles } from "../../../Definitions";
import { selectStationByLocationId } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { selectLocationsBySection } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/locationSlice";
import { selectSectionsBySystem } from "../../../Services/Agrisys_GqlDataCaller/SectionsCalls/sectionsSlice";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useAppSelector } from "../../../Store";
import { selectLocationOptionsBySystem } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { isEqual } from "lodash";


interface StartEsfWfLocation_Props {
  onClose: () => void;
  focus_view?: boolean;
}

export default function StartEsfWfLocationModal({ onClose, focus_view }: StartEsfWfLocation_Props) {
  const { t } = useTranslation();
  const { state: { system, selectedLocation, selectedStations, selectedMotors, error }, dispatch } = useEsfContext();
  const { feedMultipleByTerminal } = useSection(); 

  const [loading, setLoading] = useState<boolean>(false);
  const [cupAmount, setCupAmount] = useState<number>(1);

  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const abortControllerRef = useRef<AbortController | null>(null);

  const _fontSize = { ...(focus_view ? fonts.fontSize3 : fonts.fontSize4) }

  // #region | Data fetch
  const sections = useAppSelector((state) => selectSectionsBySystem(state, system.system_id ?? -1), isEqual);
  const locations = useAppSelector((state) => selectLocationsBySection(state, sections.map(sec => sec.section_id)), isEqual);
  const stations = useAppSelector((state) => selectStationByLocationId(state, selectedLocation?.location_id ?? -1), isEqual);

  // Options
  useSectionOptions();
  const locationOptions = useAppSelector((state) => selectLocationOptionsBySystem(state, [system.system_id]), isEqual);
  // #endregion

  // Initial ESF Context states
  useEffect(() => {
    if (!locations || locations.length === 0) return;
  
    const first = locations[0];
    if (!first?.stations) return;
  
    dispatch({
      selectedLocation: first,
      selectedSection: sections.find(s => s.section_id === first.sectionId),
      selectedStations: stations.filter(s => first.stations.includes(s.station_id))
    })
  }, [dispatch, sections]);

  // Abort prev operation
  function abortPreviousOperation() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  // Reset result
  function resetResultState() {
    abortPreviousOperation();
    setLoading(false);
    dispatch({ locationResults: [] })
  }

  // #region | Do Feeding
  const doFeeding = async () => {
    abortPreviousOperation();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    const results = await feedMultipleByTerminal(selectedMotors ?? [], true, cupAmount, undefined, abortController.signal);
    
    if (!abortControllerRef.current) return;
    
    const newLocationResults: { [key: number]: boolean } = {};
    let errorOccured: boolean = false;

    for (let i = 0; i < results.length; i++) {
      let result = results[i];
      newLocationResults[result.id] = result.success;
      if (!result.success) {
        if (!errorOccured) {
          errorOccured = true;
          dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.feedingFailed", { defaultValue: "Feeding failed" }) });
        }
      }
    }

    if (!errorOccured) {
      dispatchErrorWithTimeout(dispatch, { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) });
    }

    setLoading(false);
    dispatch({locationResults: newLocationResults})
    abortControllerRef.current = null;
  };
  // #endregion

  if (!locations || !selectedLocation) return null;

  const currentIndex = locations.findIndex((l) => l.location_id === selectedLocation?.location_id);
  const allSelected = stations.every(stationId => selectedStations?.includes(stationId));

  return (
    <NewModal
      header={t("systemControls.general.systemControls", { defaultValue: "System Controls" })}
      subHeader={selectedLocation?.name}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        headerContentHeight: focus_view ? "60px" : undefined,
        mainContentScroll: mobile ? true : false
      }}
      responsiveOverrides={{
        focus_view: focus_view,
        mobile: mobile
      }}
      predefinedButtons={{
        onClose: () => { dispatch({selectedLocation: locations[0]});  resetResultState(); /* setStaticFeedAmount(0); */ onClose() },
        onNext: () => { dispatch({selectedLocation: locations[(currentIndex + 1) % locations.length]}); resetResultState() },
        onPrev: () => { dispatch({selectedLocation: locations[(currentIndex - 1 + locations.length) % locations.length]}); resetResultState() },
      }}
      headerContent={selectedLocation ? (
        <NewSelect
          options={locationOptions ?? []}
          key={selectedLocation.location_id}
          onChange={(i) => { /*resetResultState();*/ dispatch({selectedLocation: locations.find(location => location.location_id === i?.id)})}}
          valueOverrides={{
            defaultValue: locationOptions?.find(option => option.id === selectedLocation.location_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "120px" : "160px",
            focus_view: focus_view,
            menuWidth: mobile ? "70vw" : undefined,
            menuFloat: MenuFloats.center
          }}
        />
      ) : (<></>)}
      mainContent={
        <SystemControlsInner
          activeLocationsListHeader={(t("systemControls.general.feeding", { defaultValue: "Feeding" }))}
          activeListType="Card"
          focus_view={focus_view}
          locationVisual={ selectedLocation &&
            <ESF_STATIONLIST
              key={selectedLocation.location_id}
              focus_view={focus_view}
              location={selectedLocation}
            />
          }
          activeLocationsList={
            <ActiveStationsCards/>
          }
          error={{
            errorText: error.errorText,
            elementId: error.Elementid
          }}
          selectLocation={{
            selectedLocations: selectedStations ?? [],
            isAllSelected: allSelected,
            handleSelectAll: () => {
              if (allSelected) { dispatch({ selectedStations: selectedStations?.filter(stationId => !stations.includes(stationId)) });
              } else {
                const newSelection = Array.from(new Set([...selectedStations ?? [], ...stations]));
                dispatch({ selectedStations: newSelection });
              }
            }
          }}
          selectContent={
            <div style={{ height: focus_view ? "40px" : "30px", display: "flex", alignItems: "center", gap: "10px", width: "280px"}}>
              <p style={{ ...fonts.paragraph, ..._fontSize, color: colors.greyDarkPrimary, whiteSpace: "nowrap" }}> {t("systemControls.general.staticFeeding", { defaultValue: "Static Feeding" })} </p>
                <NewInput
                  onChange={(e) => setCupAmount(Number(e))}
                  fieldOverrides={{
                    fieldStyle: NewFieldStyles.greyTertiary,
                    fieldWidth: "100%",
                    focus_view: focus_view
                  }}
                  valueOverrides={{
                    defaultValue: 1,
                    valueType: "number",
                    minValueNumber: 0
                  }}
                />
              <p style={{ ...fonts.paragraph, ..._fontSize, color: colors.greyDarkPrimary }}> { cupAmount === 1 ? t("units.cup", { defaultValue: "cup" }) : t("units.cups", { defaultValue: "cups" })} </p>
            </div>
          }
          button={{
            content: t("systemControls.general.startSelected", { defaultValue: "Start Selected" }),
            startEnhancer: "Start",
            onClick: () => {doFeeding()},
            disabled: loading || !selectedStations || selectedStations?.length === 0,
          }}
        />
      }
    />
  )
}